import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { X } from "lucide-react";
import { LeadForm } from "@/components/LeadForm";
import { REQUIREMENT_OPTIONS } from "@/data/site";
import { track } from "@/lib/analytics";

const QuoteContext = createContext({ openQuote: () => {} });

export const useQuote = () => useContext(QuoteContext);

export const QuoteProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("");

  const openQuote = useCallback((requirement) => {
    setPreset(requirement || "");
    setOpen(true);
    track("quote_click", { requirement: requirement || "General" });
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <QuoteContext.Provider value={{ openQuote }}>
      {children}
      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Request a quote"
          data-testid="quote-modal"
        >
          <button
            aria-label="Close quote form"
            className="absolute inset-0 bg-cts-ink/60 backdrop-blur-sm cursor-default"
            onClick={() => setOpen(false)}
            data-testid="quote-modal-backdrop"
          />
          <div className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-cts-line px-6 py-4 flex items-center justify-between rounded-t-2xl sm:rounded-t-xl">
              <div>
                <p className="eyebrow">REQUEST A QUOTE</p>
                <h2 className="font-heading font-bold text-cts-ink text-lg">Tell Us What You Need</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="quote-modal-close-btn"
                className="p-2 rounded-md text-cts-charcoal hover:text-cts-red hover:bg-cts-soft transition-colors"
              >
                <X size={22} />
              </button>
            </div>
            <div className="px-6 py-6">
              <LeadForm
                formType="quote"
                requirement={preset}
                requirementOptions={REQUIREMENT_OPTIONS}
                submitLabel="REQUEST QUOTE"
                trackEvent="quote_submitted"
                sourceCta="Get a Quote modal"
                onClose={() => setOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </QuoteContext.Provider>
  );
};
